import { useCan } from '../store';
import React, { useState } from 'react';
import { alertApprovalIfPending, ApiError, MsgResult } from '../api/request';
import { formatTime } from '../utils/time';
import { confirmDialog, alertDialog } from '../utils/dialog';
import SearchSelect from './SearchSelect';
import { selLayoutCls } from '../utils/searchOptions';

// 发布状态 (2026-09-03): 材料 / 设备各有 isPublished (1 未发布 / 2 已发布, 不用 0 值避开 gorm 零值坑) + publishedAt (unix 秒, 0=从未发布).
// 只约束 OpenAPI 对外下发 (参数行还要求 所属设备 与 引用材料 同时已发布), 平台内展示/编辑不受限.
// 材料列表/详情、设备管理/工况 四处共用这里的徽标与切换按钮, 不各画一份.

/**
 * 发布状态徽标 + 发布时间 (未发布但曾发布过的显示"最近一次发布时间").
 * layout: 'stack' 徽标上、时间下 (列表单元格, 列宽窄); 'inline' 同一行左右排 (详情页信息行, 避免把行撑成两倍高).
 */
/** isPublished 取值 (与后端 models.PublishStatus* 对齐): 1 未发布 / 2 已发布. */
export const PUBLISH_UNPUBLISHED = 1;
export const PUBLISH_PUBLISHED = 2;

export function isPublishedValue(status?: number): boolean {
    return status === PUBLISH_PUBLISHED;
}

export function PublishBadge({
    isPublished,
    publishedAt,
    showTime = true,
    layout = 'stack',
}: {
    isPublished?: number;
    publishedAt?: number;
    showTime?: boolean;
    layout?: 'stack' | 'inline';
}) {
    const on = isPublishedValue(isPublished);
    const inline = layout === 'inline';
    return (
        <span
            className={
                inline
                    ? 'inline-flex items-center gap-1.5'
                    : 'inline-flex flex-col items-start gap-0.5'
            }
        >
            <span
                className={`tag ${on ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}
            >
                {on ? '已发布' : '未发布'}
            </span>
            {showTime && publishedAt ? (
                <span
                    className={`${inline ? 'text-[11px]' : 'text-[10px]'} text-slate-400 whitespace-nowrap`}
                    title={on ? '发布时间' : '最近一次发布时间 (当前未发布)'}
                >
                    {formatTime(publishedAt)}
                </span>
            ) : null}
        </span>
    );
}

/** 发布状态筛选下拉 (列表页表头筛选用). 值用 '' / '1' / '2' 三态字符串, 方便同步进 URL query. */
export function PublishStatusSelect({
    value,
    onChange,
    className = 'sel w-28',
}: {
    value: string;
    onChange: (v: string) => void;
    className?: string;
}) {
    return (
        <SearchSelect
            className={selLayoutCls(className)}
            value={value}
            onChange={(v) => onChange(v)}
            placeholder="全部发布态"
            options={PUBLISH_FILTER_OPTS}
            clearable
        />
    );
}

const PUBLISH_FILTER_OPTS = [
    { value: String(PUBLISH_PUBLISHED), label: '已发布' },
    { value: String(PUBLISH_UNPUBLISHED), label: '未发布' },
];

/** 三态筛选串 → 请求参数 (空串 = 不过滤). */
export function publishedFilter(v: string): number | undefined {
    return v === '' ? undefined : Number(v);
}

/**
 * 发布 / 取消发布按钮: 统一确认文案 + 审批提示. onToggle 调对应资源的 publish 接口,
 * onDone 由调用方回读列表/详情 (走审批时数据未变, 回读只是拿回原值).
 */
export function PublishToggle({
    resource,
    published,
    label,
    onToggle,
    onDone,
    className,
}: {
    /** 所属资源域: 按钮只对有该资源 publish 权的人渲染 (2026-09-11 三档角色, 与后端路由登记同口径). */
    resource: 'material' | 'device';
    published: boolean;
    label: string;
    onToggle: (publish: boolean) => Promise<MsgResult<unknown>>;
    onDone: () => void | Promise<void>;
    className?: string;
}) {
    const [busy, setBusy] = useState(false);
    const canPublish = useCan(resource, 'publish');
    const run = async () => {
        const next = !published;
        const msg = next
            ? `确认发布 ${label}?\n发布后 OpenAPI 对下游可见 (参数行还需所属设备与引用材料同时已发布).`
            : `确认取消发布 ${label}?\n取消后 OpenAPI 不再下发该数据及关联参数行; 平台内展示/编辑不受影响.`;
        if (!(await confirmDialog(msg))) return;
        setBusy(true);
        try {
            const res = await onToggle(next);
            await onDone();
            alertApprovalIfPending(res.message);
        } catch (e) {
            alertDialog(
                `操作失败: ${e instanceof ApiError ? `${e.code}: ${e.message}` : String(e)}`,
            );
        } finally {
            setBusy(false);
        }
    };
    if (!canPublish) return null;
    return (
        <button
            type="button"
            disabled={busy}
            className={
                className ||
                (published
                    ? 'text-slate-500 hover:underline'
                    : 'text-emerald-600 hover:underline')
            }
            title="提交后走审批"
            onClick={run}
        >
            {published ? '取消发布' : '发布'}
        </button>
    );
}
